import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import AddFundsTray, {
  type AddFundsStep,
  MorphButton,
  NEW_CARD,
} from './AddFundsTray';
import type {
  BillingPaymentSourceSummary,
  CardSetupCheckoutResponse,
  PaymentSourcesResponse,
  SyncPaymentSourcesResponse,
} from '@dreamai/types';
import { useAuth0 } from '@auth0/auth0-react';
import { ReplyToItem } from '../../../contexts/ReplyToContext';
import { ReactComponent as CardAgentIcon } from '../../../icons/card-agent.svg';
import { ReactComponent as CardVisaIcon } from '../../../icons/card-visa.svg';
import { ReactComponent as CardAmexIcon } from '../../../icons/card-amex.svg';
import { ReactComponent as CardMastercardIcon } from '../../../icons/card-mastercard.svg';
import { ReactComponent as CardOtherIcon } from '../../../icons/card-other.svg';
import { ReactComponent as CreditCardXIcon } from '../../../icons/credit-card-x.svg';
import TransactionalSheet, { SheetPresentation } from './TransactionalSheet';
import {
  BuildingIcon,
  CheckIcon,
  CreditCardLockIcon,
  CreditCardPlusIcon,
  LinkIcon,
  MarkerPinIcon,
  MessageIcon,
  PasscodeIcon,
  ShoppingBagIcon,
  TruckIcon,
  UserIcon,
  XCloseIcon,
} from './transactional-icons';
import AddPaymentCardModal, {
  AddPaymentCardResult,
} from './AddPaymentCardModal';
import SpendingOnboardingModal from './SpendingOnboardingModal';
import { useOnboardingModals } from '../../../providers';
import { useOrganisationsEnabled } from '../../../hooks/useOrganisationsEnabled';
import api from '../../../api';
import {
  navigateToCheckout,
  openCheckoutPopup,
  stashCheckoutResume,
  type CheckoutResume,
} from '../../../utils/hostedCheckout';
import type {
  OrganisationSpendReadiness,
  PaymentMethodStatusResponse,
  SpendApprovalDetails,
  SpendApprovalFact,
  SpendApprovalPersonEdit,
  UpdateResponseArtifactRequest,
} from '@dreamai/types';
import {
  formatOrganisationAmount,
  isOrganisationChargeError,
  organisationChargeErrorMessage,
  organisationChargeFailure,
  type OrganisationChargeFailure,
  organisationAllowanceNote,
  organisationHeadroomLabel,
  organisationUsageLabel,
  organisationLimitHint,
  organisationUnavailableReason,
} from './organisationSpend';
import SpendApprovalPeople, {
  initialPersonValues,
  missingPersonFields,
  personEdits,
  type PersonValues,
} from './SpendApprovalPeople';
import { guestComplete, type HotelBookingGuestDetails } from './hotelArtifact';
import {
  isSecurityCodeComplete,
  securityCodeLength,
  type MandateSecurePayment,
} from './purchaseApprovalArtifact';
import './response-artifact-modal.scss';
import './spending-approval-modal.scss';
import './purchase-approval-modal.scss';

interface TopUpOption {
  priceId: string;
  unitAmount: number;
  unitsPerProduct: number;
  quantity: number;
  totalCost: number;
  totalCredits: number;
  currency: string;
}

interface SpendReadiness {
  assistantCardAvailable?: boolean;
  sufficient: boolean;
  balance: number;
  currency?: string;
  shortfall: number;
  topUpOption?: TopUpOption | null;
  billingSetupRequired?: boolean;
  /** Absent when the user is not funded by an organisation */
  organisation?: OrganisationSpendReadiness;
}

interface DepositReadiness {
  ready: boolean;
  reason?: 'negative_purchase';
  purchaseBalance: number;
  shortfall?: number;
  currency?: string;
  topUpOption?: TopUpOption | null;
  billingSetupRequired?: boolean;
}

type ModalPhase =
  | 'checking'
  | 'ready'
  | 'topup-required'
  | 'topping-up'
  | 'charging-organisation'
  | 'error';

/**
 * The card choice PATCHed back with a spending approval. `securityCode` is
 * only ever set on the mandate-as-approval card, for a saved card whose code
 * is not in the vault: the server vaults it with that card and strips it
 * before anything is stored or forwarded. It lives in component state for the
 * life of the sheet and nowhere else.
 */
export type CardSelection = NonNullable<
  UpdateResponseArtifactRequest['card_selection']
> & {
  securityCode?: string;
  /** Corrections the user made to the people on the card (name, ID, contact). */
  people?: SpendApprovalPersonEdit[];
};

const ORGANISATION_CARD = 'organisation';
const AGENT_CARD = 'agent';

/** After this long the organisation charge says it is checking the payment. */
const ORGANISATION_SLOW_CHARGE_MS = 6000;

type OrganisationProgress =
  | { stage: 'approving' | 'checking' }
  | ({ stage: 'failed' } & OrganisationChargeFailure);

const parseSpendLimit = (raw: string): number | null => {
  const cleaned = raw.replace(/[\s,£$€]/g, '');
  if (!/^\d*\.?\d+$/.test(cleaned)) return null;
  const n = Math.round(Number(cleaned) * 100) / 100;
  return n > 0 ? n : null;
};

/**
 * Slider scale for a proposed amount: a step from {1,2,5}×10^n so roughly
 * 40 stops cover the range, and a ceiling of twice the proposal rounded up to
 * a step boundary (with headroom so the proposal itself is not the maximum).
 */
const spendSliderScale = (
  proposed: number,
): { min: number; max: number; step: number } => {
  const rough = Math.max(proposed * 2, 1) / 40;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const step = [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= rough) ?? pow;
  const max = Math.max(Math.ceil((proposed * 2) / step) * step, step * 2);
  return { min: step, max, step };
};

interface SavedCard {
  id: string;
  label: string;
  cardLastFour: string | null;
  cardNetwork: string | null;
  createdAt: string;
}

/**
 * In-person actions can only be paid with a Dream-controlled card: the
 * Assistant Card while the company still has one, otherwise the organisation
 * card.
 */
const DREAM_CARD_ONLY_ACTIONS = new Set(['buy_and_act', 'bring_and_buy']);

interface SpendingApprovalModalProps {
  show: boolean;
  /**
   * For the organisation card the host should return the submit promise: a
   * rejected organisation charge is shown in the sheet and nothing is approved.
   */
  onApprove: (cardSelection?: CardSelection) => void | Promise<void>;
  onCancel: () => void;
  onReject?: () => void;
  /**
   * Way to pay to preselect when the sheet opens (e.g. the one the user chose
   * on a press that had to be re-quoted); the defaults apply when absent.
   */
  initialCardSelection?: CardSelection;
  items: ReplyToItem[];
  inline?: boolean;
  agentImageUrl?: string;
  assistantName?: string;
  isDeposit?: boolean;
  /** Payment methods permitted by this approval; absent keeps current choices. */
  acceptedPaymentMethods?: Array<
    'agent' | 'organisation' | 'user_card' | 'merchant_stored'
  >;
  /** Extra summary rows shown above the spend line (e.g. hotel, room, dates). */
  summaryRows?: SpendSheetSummaryRow[];
  /** Editable guest details; approval is disabled until they are complete. */
  guest?: HotelBookingGuestDetails;
  onGuestChange?: (guest: HotelBookingGuestDetails) => void;
  /** Caller-owned details block (e.g. flight passengers) rendered above the card chooser. */
  details?: React.ReactNode;
  /** Holds the approve button while `details` are incomplete. */
  approveDisabled?: boolean;
  /** Overrides the sheet heading, e.g. "Confirm booking". */
  title?: string;
  description?: string;
  /** Overrides the approve button label, e.g. "Book now". */
  approveLabel?: string;
  context?: string;
  /**
   * Structured description of the purchase (journey, terms, travellers…).
   * When present it replaces the item line and `context` prose.
   */
  structuredDetails?: SpendApprovalDetails;
  /**
   * The total is a fixed quoted price (e.g. a hotel rate or rail fare) rather
   * than a limit the user may edit: no amount input or slider, and the
   * approved amount is never changed.
   */
  exactAmount?: boolean;
  actionKey?: string;
  merchantName?: string;
  shareToken?: string;
  /** `modal` (default) layers over the app; `standalone` is the page. */
  presentation?: SheetPresentation;
  /**
   * Whether the sheet can be dismissed without deciding (close control,
   * Escape, scrim, Cancel). Standalone pages are never dismissible.
   */
  dismissible?: boolean;
  /**
   * Host state to stash before a same-tab hosted card checkout (mobile), so
   * the host can rebuild this modal when the checkout returns to the page.
   */
  checkoutResume?: CheckoutResume;
  /**
   * Server-provided when this approval is also the payment approval: the card
   * chosen here is the one Dream pays with at the checkout, so saved cards
   * show whether their security code is on file and grow a code box when it
   * is not. Absent for the legacy human-operator route.
   */
  securePayment?: MandateSecurePayment | null;
}

export interface SpendSheetSummaryRow {
  key: string;
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
}

function getCardIcon(network: string | null) {
  switch (network) {
    case 'visa':
      return <CardVisaIcon />;
    case 'mastercard':
      return <CardMastercardIcon />;
    case 'amex':
      return <CardAmexIcon />;
    default:
      return <CardOtherIcon />;
  }
}

const SpendingApprovalModal: React.FC<SpendingApprovalModalProps> = ({
  show,
  onApprove,
  onCancel,
  onReject,
  initialCardSelection,
  items,
  inline,
  assistantName = 'Secretary',
  isDeposit,
  acceptedPaymentMethods,
  exactAmount,
  summaryRows,
  guest,
  onGuestChange,
  details,
  approveDisabled,
  title: titleOverride,
  description: descriptionOverride,
  approveLabel,
  context,
  structuredDetails,
  actionKey,
  merchantName,
  shareToken,
  presentation = 'modal',
  dismissible = true,
  checkoutResume,
  securePayment,
}) => {
  const cardLabel = `${assistantName}\u2019s Card`;
  const canDismiss = presentation === 'modal' && dismissible;
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: authLoading,
    loginWithRedirect,
  } = useAuth0();
  const { hasSeenModal, loading: onboardingLoading } = useOnboardingModals();
  const organisationsEnabled = useOrganisationsEnabled();
  const sharedMode = Boolean(shareToken);
  const sessionExpiredMessage =
    "This session expired because you weren't logged in. Please ask Dream to send the link again.";
  const paymentMethodUnavailableMessage =
    "This payment method isn't available.";

  // The spending approval / mandate flow requires authentication (saved cards,
  // readiness checks, marking onboarding as seen). On the public shared-artifact
  // page a logged-out user would otherwise get stuck in an onboarding loop, so
  // send them to log in and return to this page.
  const needsLogin = show && !sharedMode && !authLoading && !isAuthenticated;
  useEffect(() => {
    if (needsLogin) {
      loginWithRedirect({
        appState: {
          path: window.location.pathname + window.location.search,
        },
      });
    }
  }, [needsLogin, loginWithRedirect]);

  const [phase, setPhase] = useState<ModalPhase>('checking');
  const [readiness, setReadiness] = useState<SpendReadiness | null>(null);
  const [topUpOption, setTopUpOption] = useState<TopUpOption | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [depositReadiness, setDepositReadiness] =
    useState<DepositReadiness | null>(null);

  // Card the user explicitly picked: AGENT_CARD, ORGANISATION_CARD, a
  // saved-card id, or '' for the default (derived below once readiness and
  // the saved cards are in).
  const [chosenCard, setChosenCard] = useState<string>('');
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [cardHint, setCardHint] = useState('');
  const [securityCode, setSecurityCode] = useState('');
  // Cards whose code reached the vault while this sheet was open (add-card
  // always stores it), on top of what the server said was on file.
  const [cvcNowOnFile, setCvcNowOnFile] = useState<Set<string>>(
    () => new Set(),
  );

  const cvcOnFileFor = useCallback(
    (credentialId: string): boolean | null => {
      if (!securePayment) return null;
      if (cvcNowOnFile.has(credentialId)) return true;
      const option = securePayment.instruments.find(
        (o) => o.type === 'user_card' && o.ref === credentialId,
      );
      return option ? option.cvcOnFile === true : false;
    },
    [securePayment, cvcNowOnFile],
  );

  // Accordion state: which section is expanded
  const [activeSection, setActiveSection] = useState<
    'savedCards' | 'merchantStored'
  >('savedCards');

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Airwallex card setup (hosted SETUP checkout) state. While true we poll
  // for the card landing so the modal can flip to the top-up button in place.
  const [settingUpCard, setSettingUpCard] = useState(false);
  // Assistant Card funding tray: which saved card pays the top-up.
  const [fundingOpen, setFundingOpen] = useState(false);
  const [fundingStep, setFundingStep] = useState<AddFundsStep>('pick');
  const [fundingCards, setFundingCards] = useState<
    BillingPaymentSourceSummary[]
  >([]);
  const [fundingCardsLoading, setFundingCardsLoading] = useState(false);
  const [fundingCardId, setFundingCardId] = useState<string>('');
  const spendFooterRef = useRef<HTMLDivElement>(null);

  const bookableItems = useMemo(
    () => items.filter((item) => item.bookable),
    [items],
  );

  // Hosts rebuild `items` on every render, so key the totals on their values:
  // a new array with the same prices must not restart the readiness check.
  const bookableItemsRef = useRef(bookableItems);
  bookableItemsRef.current = bookableItems;
  const totalsKey = bookableItems
    .map((item) => `${item.price_currency ?? ''}:${item.price ?? ''}`)
    .join('|');
  const totalsByCurrency = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const item of bookableItemsRef.current) {
      if (item.price != null && item.price_currency) {
        totals[item.price_currency] =
          (totals[item.price_currency] || 0) + item.price;
      }
    }
    return totals;
  }, [totalsKey]);

  const totalEntries = Object.entries(totalsByCurrency);
  const mixedCurrency = totalEntries.length > 1;
  const readinessEntry = totalEntries.find(([, amount]) => amount > 0);
  const readinessAmount = mixedCurrency
    ? (readinessEntry?.[1] ?? 0)
    : (totalEntries[0]?.[1] ?? 0);
  const totalAmount = mixedCurrency ? 0 : readinessAmount;

  // The proposed limit is a starting point: the user decides how much may be
  // spent, so the amount is editable on a single-currency purchase unless it
  // is an exact quote.
  const limitEditable =
    !isDeposit &&
    !exactAmount &&
    Object.keys(totalsByCurrency).length === 1 &&
    totalAmount > 0;
  const [limitInput, setLimitInput] = useState('');
  const sliderScale = useMemo(
    () => spendSliderScale(totalAmount),
    [totalAmount],
  );
  const editedLimit = limitEditable ? parseSpendLimit(limitInput) : null;
  const approvedAmount = editedLimit ?? totalAmount;
  const limitChanged =
    editedLimit != null && Math.abs(editedLimit - totalAmount) >= 0.005;

  // People as they will print on the ticket: editable in place, and the
  // approval is held until every required field has a value.
  const people = useMemo(
    () => structuredDetails?.people ?? [],
    [structuredDetails],
  );
  const [personValues, setPersonValues] = useState<PersonValues>(() =>
    initialPersonValues(people),
  );
  useEffect(() => {
    setPersonValues(initialPersonValues(people));
  }, [people]);
  const peopleMissing = missingPersonFields(people, personValues);
  const peopleEdits = personEdits(people, personValues);

  const limitInvalid =
    (limitEditable && editedLimit == null) || peopleMissing.length > 0;
  const withLimit = useCallback(
    (selection: CardSelection): CardSelection => ({
      ...selection,
      ...(limitChanged
        ? { approvedSpendPermission: editedLimit ?? undefined }
        : {}),
      ...(peopleEdits.length > 0 ? { people: peopleEdits } : {}),
    }),
    [limitChanged, editedLimit, peopleEdits],
  );

  const formatPrice = (amount: number, currency: string) => {
    try {
      const isWholeNumber = amount % 1 === 0;
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: isWholeNumber ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${amount} ${currency}`;
    }
  };

  const totalDisplay = totalEntries
    .map(([cur, amt]) => formatPrice(amt, cur))
    .join(' + ');
  const currency = totalEntries[0]?.[0] ?? '';
  const readinessCurrency = readinessEntry?.[0] ?? totalEntries[0]?.[0] ?? '';

  const dreamCardOnly =
    !isDeposit && !!actionKey && DREAM_CARD_ONLY_ACTIONS.has(actionKey);

  const personalPaymentMethodsAvailable = isDeposit
    ? depositReadiness !== null
    : readiness !== null && readiness.assistantCardAvailable !== false;
  // A prepared booking only offers methods that can actually pay for its fare.
  const accepts = (
    method: 'agent' | 'organisation' | 'user_card' | 'merchant_stored',
  ) => !acceptedPaymentMethods || acceptedPaymentMethods.includes(method);
  const agentCardOffered =
    !isDeposit && personalPaymentMethodsAvailable && accepts('agent');

  // Organisation-funded option: purchases only (never deposits), and only when
  // the server says the user is funded by an organisation. Funding is a data
  // fact the server reports regardless of the `organisations.enabled` flag;
  // the flag gates visibility, so the row is hidden while it is off for the
  // signed-in user. A logged-out visitor on the shared page cannot be asked
  // about their flag, so the server's answer stands there.
  const organisationVisible = isAuthenticated
    ? organisationsEnabled === true
    : sharedMode;
  const organisation =
    isDeposit || !organisationVisible || !accepts('organisation')
      ? undefined
      : readiness?.organisation;

  // Default: the saved card a replaced approval used, while it is still
  // saved; else the organisation card whenever it can fund the purchase, else
  // the Assistant Card while the company still has one, else the user's first
  // saved card (never offered for in-person purchases). While the pot is
  // still live, a deposit has no default: a negative pot balance must be
  // cleared first unless the user explicitly picks one of their own cards.
  const preferredSavedCard = personalPaymentMethodsAvailable
    ? savedCards.find((card) => card.id === securePayment?.preferredRef)
    : undefined;
  const chosenCardKnown =
    (chosenCard === ORGANISATION_CARD &&
      !mixedCurrency &&
      accepts('organisation')) ||
    (personalPaymentMethodsAvailable &&
      chosenCard === AGENT_CARD &&
      !mixedCurrency &&
      accepts('agent')) ||
    (personalPaymentMethodsAvailable &&
      accepts('user_card') &&
      savedCards.some((card) => card.id === chosenCard));
  const selectedCard =
    (chosenCardKnown ? chosenCard : '') ||
    (preferredSavedCard && !dreamCardOnly && accepts('user_card')
      ? preferredSavedCard.id
      : undefined) ||
    (organisation?.available && !mixedCurrency
      ? ORGANISATION_CARD
      : agentCardOffered && !mixedCurrency
        ? AGENT_CARD
        : personalPaymentMethodsAvailable &&
            !dreamCardOnly &&
            !isDeposit &&
            accepts('user_card') &&
            savedCards[0]
          ? savedCards[0].id
          : '');

  const isMerchantStoredSelected =
    activeSection === 'merchantStored' && accepts('merchant_stored');
  const isAgentCardSelected =
    !isMerchantStoredSelected && selectedCard === AGENT_CARD;
  const isOrganisationCardSelected =
    !isMerchantStoredSelected && selectedCard === ORGANISATION_CARD;
  const isUserCardSelected =
    !isMerchantStoredSelected &&
    selectedCard !== AGENT_CARD &&
    selectedCard !== ORGANISATION_CARD &&
    selectedCard !== '';

  const selectedSavedCard = isUserCardSelected
    ? savedCards.find((card) => card.id === selectedCard)
    : undefined;
  const needsSecurityCode =
    !!securePayment?.cvcRequired &&
    !!selectedSavedCard &&
    cvcOnFileFor(selectedSavedCard.id) !== true;
  const securityCodeComplete =
    !needsSecurityCode ||
    isSecurityCodeComplete(
      securityCode,
      selectedSavedCard?.cardNetwork ?? null,
    );

  // ── Fetch saved payment cards ────────────────────────────────────
  const fetchSavedCards = useCallback(async () => {
    try {
      const response = sharedMode
        ? await api.get<{ cards: SavedCard[] }>(
            `/memories/payment-cards?shareToken=${encodeURIComponent(shareToken || '')}`,
          )
        : await api.get<{ cards: SavedCard[] }>('/memories/payment-cards', {
            headers: {
              Authorization: `Bearer ${await getAccessTokenSilently()}`,
            },
          });
      const { data } = response;
      setSavedCards(data.cards || []);
    } catch (err: any) {
      if (err?.response?.data?.code === 'PAYMENT_METHOD_UNAVAILABLE') {
        setErrorMessage(paymentMethodUnavailableMessage);
        setPhase('error');
        return;
      }
      if (
        sharedMode &&
        (err?.response?.status === 410 ||
          err?.response?.data?.code === 'PAYMENT_SESSION_EXPIRED')
      ) {
        setErrorMessage(sessionExpiredMessage);
        setPhase('error');
        return;
      }
      console.error('Error fetching payment cards:', err);
    }
  }, [
    getAccessTokenSilently,
    paymentMethodUnavailableMessage,
    sessionExpiredMessage,
    shareToken,
    sharedMode,
  ]);

  // ── Check spend readiness on open ──────────────────────────────────
  const checkReadiness = useCallback(
    async (amount: number) => {
      // For purchases, skip check if no amount (nothing to pay for)
      if (!isDeposit && (!amount || amount <= 0)) {
        setPhase('ready');
        return;
      }
      setReadiness(null);
      setTopUpOption(null);
      setPhase('checking');
      setErrorMessage(null);

      try {
        if (isDeposit) {
          // Deposit flow: only check that purchase credit balance is non-negative
          const { data } = await api.post<DepositReadiness>(
            '/credits/check-deposit-readiness',
            sharedMode ? { shareToken } : {},
            sharedMode
              ? undefined
              : {
                  headers: {
                    Authorization: `Bearer ${await getAccessTokenSilently()}`,
                  },
                },
          );

          setDepositReadiness(data);

          if (data.ready) {
            setPhase('ready');
          } else {
            // Negative purchase balance — need top-up to clear debt
            setTopUpOption(data.topUpOption || null);
            setPhase('topup-required');
          }
          return;
        }

        // Purchase flow: check purchase credit balance (and, when the user is
        // funded by an organisation, the organisation's headroom for this amount)
        const { data } = await api.post<SpendReadiness>(
          '/credits/check-spend-readiness',
          sharedMode
            ? { amount, currency: readinessCurrency, shareToken }
            : { amount, currency: readinessCurrency },
          sharedMode
            ? undefined
            : {
                headers: {
                  Authorization: `Bearer ${await getAccessTokenSilently()}`,
                },
              },
        );

        setReadiness(data);

        if (data.assistantCardAvailable === false) {
          setTopUpOption(null);
          setPhase('ready');
        } else if (data.sufficient) {
          setPhase('ready');
        } else {
          setTopUpOption(data.topUpOption || null);
          setPhase('topup-required');
        }
      } catch (err: any) {
        if (err?.response?.data?.code === 'PAYMENT_METHOD_UNAVAILABLE') {
          setErrorMessage(paymentMethodUnavailableMessage);
          setPhase('error');
          return;
        }
        if (
          sharedMode &&
          (err?.response?.status === 410 ||
            err?.response?.data?.code === 'PAYMENT_SESSION_EXPIRED')
        ) {
          setErrorMessage(sessionExpiredMessage);
          setPhase('error');
          return;
        }
        console.error('Error checking spend readiness:', err);
        // On error, allow the user to proceed — the backend safety net
        // in the human-task creation flow will catch any actual shortfall.
        setPhase('ready');
      }
    },
    [
      readinessCurrency,
      isDeposit,
      getAccessTokenSilently,
      paymentMethodUnavailableMessage,
      sessionExpiredMessage,
      shareToken,
      sharedMode,
    ],
  );

  // Read at open time only: a changing preselection must not reset a sheet
  // the user is already interacting with.
  const initialCardSelectionRef = useRef(initialCardSelection);
  initialCardSelectionRef.current = initialCardSelection;

  // Readiness, saved cards and the editable fields are set up once per
  // opening; re-renders of the parent (new chat messages, artifact refreshes)
  // must not wipe the limit or card the user has already chosen.
  const openedRef = useRef(false);

  // A submission in progress owns the phase: re-checking readiness underneath
  // it would drop "Processing..." and re-enable the approve button.
  const submitting =
    phase === 'topping-up' || phase === 'charging-organisation';
  const submittingRef = useRef(false);
  submittingRef.current = submitting;

  useEffect(() => {
    if (show && needsLogin) {
      // Logged out: skip the auth-requiring readiness/cards/onboarding work; a
      // redirect to login is in flight.
      return;
    }
    if (show && submittingRef.current) return;
    if (show) {
      // Determine which onboarding key applies
      const onboardingKey = dreamCardOnly
        ? 'spending_onboarding_agent_only'
        : 'spending_onboarding_both';

      if (!sharedMode && !onboardingLoading && !hasSeenModal(onboardingKey)) {
        setShowOnboarding(true);
      } else {
        setShowOnboarding(false);
      }

      if (openedRef.current) return;
      openedRef.current = true;
      checkReadiness(readinessAmount);
      fetchSavedCards();
      const initial = initialCardSelectionRef.current;
      setChosenCard(
        initial?.cardType === 'organisation'
          ? ORGANISATION_CARD
          : initial?.cardType === 'agent'
            ? AGENT_CARD
            : initial?.cardType === 'user_card'
              ? (initial.credentialId ?? '')
              : '',
      );
      setActiveSection(
        initial?.cardType === 'merchant_stored'
          ? 'merchantStored'
          : 'savedCards',
      );
      setCardHint(
        initial?.cardType === 'merchant_stored' ? (initial.cardHint ?? '') : '',
      );
      setLimitInput(totalAmount > 0 ? String(totalAmount) : '');
    } else {
      openedRef.current = false;
      setPhase('checking');
      setReadiness(null);
      setDepositReadiness(null);
      setTopUpOption(null);
      setErrorMessage(null);
      setSavedCards([]);
      setShowOnboarding(false);
      setSettingUpCard(false);
      setActiveSection('savedCards');
      setCardHint('');
      setLimitInput('');
    }
  }, [
    show,
    totalAmount,
    readinessAmount,
    checkReadiness,
    fetchSavedCards,
    dreamCardOnly,
    onboardingLoading,
    hasSeenModal,
    sharedMode,
  ]);

  // ── Top up & approve (single action) ─────────────────────────────
  const handleTopupAndApprove = useCallback(async () => {
    if (!topUpOption) return;

    setPhase('topping-up');
    setErrorMessage(null);

    try {
      const paymentSourceId =
        fundingCardId && fundingCardId !== NEW_CARD ? fundingCardId : undefined;
      const { data } = sharedMode
        ? await api.post('/credits/topup-balance', {
            priceId: topUpOption.priceId,
            quantity: topUpOption.quantity,
            paymentSourceId,
            shareToken,
          })
        : await api.post(
            '/credits/topup-balance',
            {
              priceId: topUpOption.priceId,
              quantity: topUpOption.quantity,
              paymentSourceId,
            },
            {
              headers: {
                Authorization: `Bearer ${await getAccessTokenSilently()}`,
              },
            },
          );

      if (data.success) {
        // Balance is now sufficient — proceed to approve immediately
        onApprove(withLimit({ cardType: 'agent' }));
      } else {
        setPhase('topup-required');
        setFundingStep('pick');
        setErrorMessage('Top-up failed. Please try again.');
      }
    } catch (err: any) {
      if (err?.response?.data?.code === 'PAYMENT_METHOD_UNAVAILABLE') {
        setErrorMessage(paymentMethodUnavailableMessage);
        setPhase('error');
        return;
      }
      if (
        sharedMode &&
        (err?.response?.status === 410 ||
          err?.response?.data?.code === 'PAYMENT_SESSION_EXPIRED')
      ) {
        setErrorMessage(sessionExpiredMessage);
        setPhase('error');
        return;
      }
      console.error('Error topping up balance:', err);
      setPhase('topup-required');
      setFundingStep('pick');
      setErrorMessage('Top-up failed. Please try again.');
    }
  }, [
    fundingCardId,
    topUpOption,
    getAccessTokenSilently,
    onApprove,
    withLimit,
    sharedMode,
    shareToken,
    paymentMethodUnavailableMessage,
    sessionExpiredMessage,
  ]);

  // ── Approve with the organisation card ───────────────────────────
  // The organisation is charged when the approval is submitted; a declined or
  // blocked charge leaves nothing approved, so show the reason here and let
  // the user pick another way to pay (never a personal top-up). The footer
  // shows the charge's progress, since it can take ~15 s.
  const [organisationProgress, setOrganisationProgress] =
    useState<OrganisationProgress | null>(null);
  const slowChargeTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(slowChargeTimer.current), []);
  useEffect(() => {
    if (show) return;
    window.clearTimeout(slowChargeTimer.current);
    setOrganisationProgress(null);
  }, [show]);

  const handleApproveWithOrganisation = useCallback(async () => {
    if (!organisation?.available) return;

    // Pin the row so a failed charge keeps showing the organisation's reason
    // rather than silently falling back to another card.
    setChosenCard(ORGANISATION_CARD);
    setPhase('charging-organisation');
    setErrorMessage(null);
    setOrganisationProgress({ stage: 'approving' });
    window.clearTimeout(slowChargeTimer.current);
    slowChargeTimer.current = window.setTimeout(
      () => setOrganisationProgress({ stage: 'checking' }),
      ORGANISATION_SLOW_CHARGE_MS,
    );

    try {
      await onApprove(withLimit({ cardType: 'organisation' }));
      window.clearTimeout(slowChargeTimer.current);
    } catch (err: unknown) {
      window.clearTimeout(slowChargeTimer.current);
      const failure = organisationChargeFailure(
        err,
        organisation.organisationName,
      );
      if (isOrganisationChargeError(err)) {
        // Headroom may have changed underneath us; re-read it, then surface
        // the failure (checkReadiness clears the error message while it runs).
        await checkReadiness(approvedAmount);
      } else {
        console.error('Error approving with the organisation card:', err);
        setPhase('ready');
      }
      if (failure) {
        setOrganisationProgress({ stage: 'failed', ...failure });
      } else {
        setOrganisationProgress(null);
        setErrorMessage(
          organisationChargeErrorMessage(err, organisation.organisationName),
        );
      }
    }
  }, [organisation, onApprove, withLimit, checkReadiness, approvedAmount]);

  // Add a card (Airwallex hosted SETUP checkout). Saves a payment method for
  // future top-ups — no subscription is needed. Shared (logged-out) mode
  // redirects in the same tab and returns to the artifact; logged-in desktop
  // opens a popup and polls so chat state is kept, logged-in mobile redirects
  // in the same tab (see hostedCheckout.ts).
  const setupPopupRef = useRef<Window | null>(null);
  const checkoutResumeRef = useRef<CheckoutResume | undefined>(checkoutResume);
  checkoutResumeRef.current = checkoutResume;

  const handleAddCard = useCallback(async () => {
    setErrorMessage(null);

    if (sharedMode) {
      try {
        const { data } = await api.post(
          `/public/artifacts/${encodeURIComponent(shareToken || '')}/setup-payment-method`,
          {},
        );
        if (data.success && data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
          return;
        }
        setErrorMessage(
          data.error || 'Failed to start card setup. Please try again.',
        );
      } catch (err: any) {
        if (err?.response?.data?.code === 'PAYMENT_METHOD_UNAVAILABLE') {
          setErrorMessage(paymentMethodUnavailableMessage);
          setPhase('error');
          return;
        }
        if (
          err?.response?.status === 410 ||
          err?.response?.data?.code === 'PAYMENT_SESSION_EXPIRED'
        ) {
          setErrorMessage(sessionExpiredMessage);
          setPhase('error');
          return;
        }
        setErrorMessage(
          err?.response?.data?.error ||
            'Failed to start card setup. Please try again.',
        );
      }
      return;
    }

    // Open the popup synchronously so popup blockers allow it, then point it
    // at the hosted checkout once the URL arrives.
    const popup = openCheckoutPopup();
    try {
      const token = await getAccessTokenSilently();
      const { data } = await api.post(
        '/accounts/update-payment-method',
        { returnUrl: window.location.pathname + window.location.search },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data.success && data.checkoutUrl) {
        if (popup && !popup.closed) {
          popup.location.href = data.checkoutUrl;
          setupPopupRef.current = popup;
          setSettingUpCard(true);
        } else {
          // Mobile or popup blocked — navigate in this tab; returnUrl brings
          // us back and the host rebuilds this modal from the stashed state.
          if (checkoutResumeRef.current) {
            stashCheckoutResume(checkoutResumeRef.current);
          }
          navigateToCheckout(data.checkoutUrl);
        }
        return;
      }
      popup?.close();
      setErrorMessage(
        data.error || 'Failed to start card setup. Please try again.',
      );
    } catch (err) {
      popup?.close();
      console.error('Error starting card setup:', err);
      setErrorMessage('Failed to start card setup. Please try again.');
    }
  }, [
    sharedMode,
    shareToken,
    getAccessTokenSilently,
    paymentMethodUnavailableMessage,
    sessionExpiredMessage,
  ]);

  const creditsPost = useCallback(
    async <T,>(path: string, body: Record<string, unknown>): Promise<T> => {
      if (sharedMode) {
        const { data } = await api.post<T>(path, { ...body, shareToken });
        return data;
      }
      const { data } = await api.post<T>(path, body, {
        headers: { Authorization: `Bearer ${await getAccessTokenSilently()}` },
      });
      return data;
    },
    [sharedMode, shareToken, getAccessTokenSilently],
  );

  // Load the company's saved cards when the funding tray opens.
  useEffect(() => {
    if (!fundingOpen) return;
    let cancelled = false;
    setFundingCardsLoading(true);
    creditsPost<PaymentSourcesResponse>('/credits/payment-sources', {})
      .then((data) => {
        if (cancelled) return;
        setFundingCards(data.cards);
        setFundingCardId((current) =>
          current === NEW_CARD || data.cards.some((c) => c.id === current)
            ? current
            : ((data.cards.find((c) => c.isDefault) ?? data.cards[0])?.id ??
              NEW_CARD),
        );
        setFundingStep((step) =>
          step === 'topping' ? step : data.cards.length ? 'pick' : 'add-card',
        );
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error loading payment sources:', err);
        setErrorMessage('Could not load your saved cards. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setFundingCardsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fundingOpen, creditsPost]);

  const handleSelectFundingCard = useCallback((id: string) => {
    setFundingCardId(id);
    setFundingStep(id === NEW_CARD ? 'add-card' : 'pick');
  }, []);

  const startFundingCardSetup = useCallback(async () => {
    const data = await creditsPost<CardSetupCheckoutResponse>(
      '/credits/payment-sources/setup',
      { returnUrl: window.location.href },
    );
    if (!data.success || !data.clientSecret || !data.env) {
      throw new Error(data.error || 'Failed to start card setup');
    }
    return { clientSecret: data.clientSecret, env: data.env };
  }, [creditsPost]);

  const handleFundingCardSaved = useCallback(async () => {
    const data = await creditsPost<SyncPaymentSourcesResponse>(
      '/credits/payment-sources/sync',
      { knownIds: fundingCards.map((c) => c.id) },
    );
    setFundingCards(data.cards);
    const next =
      data.newCard ?? data.cards.find((c) => c.isDefault) ?? data.cards[0];
    if (next) setFundingCardId(next.id);
    setFundingStep('pick');
  }, [creditsPost, fundingCards]);

  const handleAddFunds = useCallback(() => {
    setFundingStep('topping');
    void handleTopupAndApprove();
  }, [handleTopupAndApprove]);

  // Poll for the card landing while the hosted checkout is open in the popup.
  useEffect(() => {
    if (!settingUpCard || phase !== 'topup-required') return;

    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const { data } = sharedMode
          ? await api.post<PaymentMethodStatusResponse>(
              '/credits/payment-method-status',
              { shareToken },
            )
          : await api.post<PaymentMethodStatusResponse>(
              '/credits/payment-method-status',
              {},
              {
                headers: {
                  Authorization: `Bearer ${await getAccessTokenSilently()}`,
                },
              },
            );
        if (cancelled) return;
        if (data.hasCard) {
          setupPopupRef.current?.close();
          setupPopupRef.current = null;
          setSettingUpCard(false);
          checkReadiness(approvedAmount);
        } else if (setupPopupRef.current?.closed) {
          // The user closed the checkout without saving a card — stop
          // waiting and re-enable the button so they can retry.
          setupPopupRef.current = null;
          setSettingUpCard(false);
        }
      } catch {
        // Transient poll errors are fine — keep polling.
      }
    }, 4000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    settingUpCard,
    phase,
    sharedMode,
    shareToken,
    getAccessTokenSilently,
    checkReadiness,
    approvedAmount,
  ]);

  const handleAddCardSuccess = useCallback((card: AddPaymentCardResult) => {
    setSavedCards((prev) => [
      {
        id: card.credentialId,
        label: card.label,
        cardLastFour: card.cardLastFour,
        cardNetwork: card.cardNetwork,
        createdAt: new Date().toISOString(),
      },
      ...prev,
    ]);
    setCvcNowOnFile((prev) => new Set(prev).add(card.credentialId));
    setChosenCard(card.credentialId);
    setShowAddCardModal(false);
  }, []);

  const organisationUnavailable = organisation
    ? organisationUnavailableReason(organisation)
    : undefined;
  const organisationLimit = organisation
    ? organisationLimitHint(organisation)
    : undefined;
  const organisationAllowance =
    organisation && isOrganisationCardSelected && !organisationUnavailable
      ? organisationAllowanceNote(organisation)
      : undefined;

  // The funding tray replaces the top-up button for purchases paid with the
  // Assistant Card; deposits keep today's flow.
  const fundingTrayActive =
    isAgentCardSelected &&
    !isDeposit &&
    !!topUpOption &&
    (phase === 'topup-required' || phase === 'topping-up');

  useEffect(() => {
    if (!fundingTrayActive) setFundingOpen(false);
  }, [fundingTrayActive]);

  // ── Resolve the action button label & handler ──────────────────────
  const getActionButton = () => {
    // One submission at a time, whichever card is highlighted meanwhile.
    if (submitting) {
      return {
        label: 'Processing...',
        onClick: undefined as (() => void) | undefined,
        disabled: true,
      };
    }

    // Organisation card: admin funded, so there is never a top-up to offer —
    // either the amount fits the remaining limit or the button stays off.
    if (isOrganisationCardSelected) {
      if (phase === 'checking') {
        return {
          label: 'Checking balance...',
          onClick: undefined as (() => void) | undefined,
          disabled: true,
        };
      }
      if (!organisation?.available) {
        return {
          label: 'Not available for this amount',
          onClick: undefined as (() => void) | undefined,
          disabled: true,
        };
      }
      return {
        label: 'Approve spend',
        onClick: handleApproveWithOrganisation,
        disabled: limitInvalid,
      };
    }

    // If user card is selected, bypass balance checks entirely
    if (isUserCardSelected) {
      return {
        label: isDeposit ? 'Approve reservation' : 'Approve spend',
        onClick: () =>
          onApprove(
            withLimit({
              cardType: 'user_card',
              credentialId: selectedCard,
              ...(needsSecurityCode ? { securityCode } : {}),
            }),
          ),
        disabled: limitInvalid || !securityCodeComplete,
      };
    }

    // Merchant-stored card: no balance check needed
    if (isMerchantStoredSelected) {
      return {
        label: 'Approve spend',
        onClick: () =>
          onApprove(
            withLimit({
              cardType: 'merchant_stored',
              ...(cardHint.trim() ? { cardHint: cardHint.trim() } : {}),
            }),
          ),
        disabled: limitInvalid,
      };
    }

    // Nothing selectable yet: either the organisation row is still loading,
    // or the user has no card to pay with. (A deposit blocked by a negative
    // pot balance falls through to the top-up actions below.)
    if (!isAgentCardSelected && !(isDeposit && phase === 'topup-required')) {
      if (phase === 'checking') {
        return {
          label: 'Checking payment options...',
          onClick: undefined as (() => void) | undefined,
          disabled: true,
        };
      }
      if (
        phase === 'error' &&
        sharedMode &&
        errorMessage === sessionExpiredMessage
      ) {
        return {
          label: 'Session expired',
          onClick: undefined as (() => void) | undefined,
          disabled: true,
        };
      }
      return {
        label: isDeposit ? 'Approve reservation' : 'Approve spend',
        onClick: undefined as (() => void) | undefined,
        disabled: true,
      };
    }

    switch (phase) {
      case 'checking':
        return {
          label: 'Checking balance...',
          onClick: undefined as (() => void) | undefined,
          disabled: true,
        };
      case 'ready':
        return {
          label: 'Approve spend',
          onClick: () => onApprove(withLimit({ cardType: 'agent' })),
          disabled: limitInvalid,
        };
      case 'topup-required':
        if (fundingTrayActive) {
          return {
            label: `Add funds to ${cardLabel}`,
            onClick: () => setFundingOpen(true),
            disabled: fundingOpen,
          };
        }
        if (
          readiness?.billingSetupRequired ||
          depositReadiness?.billingSetupRequired
        ) {
          return {
            label: settingUpCard ? 'Waiting for your card…' : 'Add a card',
            onClick: settingUpCard ? undefined : handleAddCard,
            disabled: settingUpCard,
          };
        }
        if (topUpOption) {
          return {
            label: isDeposit
              ? `Top-up ${formatPrice(topUpOption.totalCredits, topUpOption.currency)} and reserve`
              : `Top-up ${formatPrice(topUpOption.totalCredits, topUpOption.currency)} and approve`,
            onClick: handleTopupAndApprove,
            disabled: false,
          };
        }
        if (sharedMode) {
          return {
            label: 'Insufficient balance',
            onClick: undefined as (() => void) | undefined,
            disabled: true,
          };
        }
        return {
          label: 'Go to Billing',
          onClick: () => window.open('/settings/plan-and-topups', '_blank'),
          disabled: false,
        };
      case 'error':
        if (sharedMode && errorMessage === sessionExpiredMessage) {
          return {
            label: 'Session expired',
            onClick: undefined as (() => void) | undefined,
            disabled: true,
          };
        }
        return {
          label: 'Try anyway',
          onClick: () => onApprove(withLimit({ cardType: 'agent' })),
          disabled: limitInvalid,
        };
    }
  };

  const guestIncomplete = guest != null && !guestComplete(guest);
  const baseAction = getActionButton();
  const action = {
    ...baseAction,
    label:
      approveLabel && baseAction.onClick && !baseAction.disabled
        ? approveLabel
        : baseAction.label,
    disabled:
      baseAction.disabled || guestIncomplete || Boolean(approveDisabled),
  };

  const title =
    titleOverride ?? (isDeposit ? 'Confirm deposit' : 'Confirm spend');
  const description =
    descriptionOverride ??
    (isDeposit
      ? "I'm going to make a reservation on your behalf."
      : securePayment
        ? "I'm going to make a purchase on your behalf and pay at the checkout with the card you choose here, up to this limit. You won't be asked to approve the payment again."
        : "I'm going to make a purchase on your behalf.");

  const summaryRow = (
    key: string,
    icon: React.ReactNode,
    label: string,
    value: React.ReactNode,
  ) => (
    <div className='spend-sheet-field' key={key}>
      <span className='spend-sheet-field-icon'>{icon}</span>
      <div className='spend-sheet-field-text'>
        <label>{label}</label>
        <div className='spend-sheet-value'>{value}</div>
      </div>
    </div>
  );

  const joinLines = (
    lines: { key: string; node: React.ReactNode }[],
  ): React.ReactNode =>
    lines.map((line, index) => (
      <React.Fragment key={line.key}>
        {index > 0 && <br />}
        {line.node}
      </React.Fragment>
    ));

  const factList = (facts: SpendApprovalFact[], className: string) => (
    <dl className={className}>
      {facts.map((fact) => (
        <div className='spend-sheet-fact' key={fact.label}>
          <dt>{fact.label}</dt>
          <dd>{fact.value}</dd>
        </div>
      ))}
    </dl>
  );

  const orderRows: React.ReactNode[] = [];
  for (const row of summaryRows ?? []) {
    orderRows.push(
      summaryRow(row.key, row.icon ?? <MessageIcon />, row.label, row.value),
    );
  }
  if (structuredDetails) {
    orderRows.push(
      summaryRow(
        'details',
        <ShoppingBagIcon />,
        structuredDetails.headline,
        <div className='spend-sheet-details'>
          {structuredDetails.subline && (
            <div className='spend-sheet-details-subline'>
              {structuredDetails.subline}
            </div>
          )}
          {structuredDetails.terms && structuredDetails.terms.length > 0 && (
            <ul className='spend-sheet-terms' aria-label='Conditions'>
              {structuredDetails.terms.map((term) => (
                <li className='spend-sheet-term' key={term}>
                  {term}
                </li>
              ))}
            </ul>
          )}
          {structuredDetails.facts &&
            structuredDetails.facts.length > 0 &&
            factList(structuredDetails.facts, 'spend-sheet-facts')}
        </div>,
      ),
    );
  } else if (bookableItems.length > 0 && !summaryRows?.length) {
    orderRows.push(
      summaryRow(
        'items',
        <ShoppingBagIcon />,
        isDeposit ? 'Reserving' : bookableItems.length === 1 ? 'Item' : 'Items',
        joinLines(
          bookableItems.map((item, index) => ({
            key: item.id || String(index),
            node: item.descriptionLine,
          })),
        ),
      ),
    );
  }
  if (limitEditable) {
    const recheckOrganisation = (parsed: number | null) => {
      if (parsed != null && organisation && !submittingRef.current)
        checkReadiness(parsed);
    };
    orderRows.push(
      summaryRow(
        'limit',
        <CreditCardLockIcon />,
        "I won't spend more than",
        <span className='spend-sheet-limit-edit'>
          <span className='spend-sheet-limit-currency'>
            {formatPrice(0, currency).replace(/[\d.,\s]/g, '') || currency}
          </span>
          <input
            type='text'
            inputMode='decimal'
            className='spend-sheet-limit-input'
            aria-label='Spend limit'
            value={limitInput}
            disabled={submitting}
            onChange={(e) => setLimitInput(e.target.value)}
            onBlur={() => {
              const parsed = parseSpendLimit(limitInput);
              if (parsed != null) setLimitInput(String(parsed));
              recheckOrganisation(parsed);
            }}
          />
          {limitChanged && (
            <span className='spend-sheet-limit-note'>
              suggested {totalDisplay}
            </span>
          )}
          {limitInvalid && (
            <span className='spend-sheet-limit-note spend-sheet-limit-note--error'>
              Enter an amount above 0
            </span>
          )}
          <input
            type='range'
            className='spend-sheet-limit-slider'
            aria-label='Spend limit slider'
            min={sliderScale.min}
            max={sliderScale.max}
            step={sliderScale.step}
            value={Math.min(
              Math.max(editedLimit ?? totalAmount, sliderScale.min),
              sliderScale.max,
            )}
            disabled={submitting}
            onChange={(e) => setLimitInput(e.target.value)}
            onPointerUp={() => recheckOrganisation(parseSpendLimit(limitInput))}
          />
        </span>,
      ),
    );
  } else if (!isDeposit && totalDisplay) {
    orderRows.push(
      summaryRow(
        'limit',
        <CreditCardLockIcon />,
        exactAmount ? "I'll spend exactly" : "I won't spend more than",
        <span className={exactAmount ? 'spend-sheet-exact-amount' : undefined}>
          {totalDisplay}
        </span>,
      ),
    );
  }
  if (structuredDetails?.people && structuredDetails.people.length > 0) {
    orderRows.push(
      summaryRow(
        'people',
        <UserIcon />,
        structuredDetails.people.length === 1
          ? 'In the name of'
          : 'In the names of',
        <SpendApprovalPeople
          people={people}
          values={personValues}
          onChange={setPersonValues}
        />,
      ),
    );
  }
  if (structuredDetails?.notice) {
    orderRows.push(
      <p className='spend-sheet-notice' key='notice'>
        {structuredDetails.notice}
      </p>,
    );
  }

  const detailRows: React.ReactNode[] = [];
  if (!isDeposit && bookableItems.some((item) => item.purchaseLink)) {
    detailRows.push(
      summaryRow(
        'link',
        <LinkIcon />,
        'Link',
        joinLines(
          bookableItems
            .filter((item) => item.purchaseLink)
            .map((item, index) => ({
              key: item.id || String(index),
              node: (
                <a
                  href={item.purchaseLink}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {item.purchaseLink}
                </a>
              ),
            })),
        ),
      ),
    );
  }
  if (!isDeposit && bookableItems.some((item) => item.delivery)) {
    detailRows.push(
      summaryRow(
        'delivery',
        <TruckIcon />,
        'Delivery',
        joinLines(
          bookableItems
            .filter((item) => item.delivery)
            .map((item, index) => ({
              key: item.id || String(index),
              node: item.delivery,
            })),
        ),
      ),
    );
  }
  if (!isDeposit && bookableItems.some((item) => item.deliveryAddress)) {
    detailRows.push(
      summaryRow(
        'address',
        <MarkerPinIcon />,
        'Deliver to',
        joinLines(
          bookableItems
            .filter((item) => item.deliveryAddress)
            .map((item, index) => ({
              key: item.id || String(index),
              node: item.deliveryAddress,
            })),
        ),
      ),
    );
  }
  if (context && !structuredDetails) {
    detailRows.push(summaryRow('context', <MessageIcon />, 'Context', context));
  }

  const spendHead = (
    <div className='tx-head'>
      <h1 className='tx-title' id='spend-sheet-title'>
        {title}
      </h1>
      <p className='tx-sub'>{description}</p>
    </div>
  );

  const guestField = (
    field: keyof HotelBookingGuestDetails,
    label: string,
    type: 'text' | 'email' | 'tel',
    autoComplete: string,
  ) =>
    guest && onGuestChange ? (
      <label className='spend-sheet-guest-field' key={field}>
        <span>{label}</span>
        <input
          type={type}
          autoComplete={autoComplete}
          value={guest[field] ?? ''}
          onChange={(e) => onGuestChange({ ...guest, [field]: e.target.value })}
        />
      </label>
    ) : null;

  const spendBody = (
    <div className='spend-sheet-body'>
      {orderRows.length > 0 && (
        <div className='spend-sheet-summary'>{orderRows}</div>
      )}

      {guest && onGuestChange && (
        <div className='spend-sheet-guest'>
          <label className='spend-sheet-chooser-label'>Guest</label>
          <div className='spend-sheet-guest-grid'>
            {guestField('firstName', 'First name', 'text', 'given-name')}
            {guestField('lastName', 'Last name', 'text', 'family-name')}
            {guestField('email', 'Email', 'email', 'email')}
            {guestField('phone', 'Phone (optional)', 'tel', 'tel')}
          </div>
          {guestIncomplete && (
            <p className='spend-sheet-guest-note'>
              The hotel needs the guest’s first name, last name and email.
            </p>
          )}
        </div>
      )}

      {details}

      {/* Choose card section — accordion behaviour */}
      <div className='spend-sheet-card-chooser'>
        {activeSection === 'savedCards' ? (
          <>
            <label className='spend-sheet-chooser-label'>Payment methods</label>

            {/* Organisation card — purchase flow, organisation-funded users only */}
            {organisation && (
              <button
                className={`card-choice card-choice--organisation ${isOrganisationCardSelected ? 'card-choice--selected' : ''} ${organisationUnavailable || mixedCurrency ? 'card-choice--unavailable' : ''} ${organisationLimit ? 'card-choice--with-note' : ''}`}
                onClick={() => setChosenCard(ORGANISATION_CARD)}
                type='button'
                disabled={submitting || mixedCurrency}
                aria-pressed={isOrganisationCardSelected}
                aria-describedby={
                  organisationLimit
                    ? 'spend-sheet-organisation-limit'
                    : organisationUnavailable
                      ? 'spend-sheet-organisation-reason'
                      : undefined
                }
              >
                <div className='card-left'>
                  <BuildingIcon />
                  <span className='card-nickname'>
                    {organisation.organisationName}
                  </span>
                </div>
                <div className='card-right'>
                  <div className='card-right-column'>
                    <span className='card-balance-label'>
                      {organisationUnavailable
                        ? 'Not available'
                        : 'Paid by your organisation'}
                    </span>
                    <span className='card-balance-value'>
                      {organisationUnavailable
                        ? organisationHeadroomLabel(organisation)
                        : organisationUsageLabel(organisation)}
                    </span>
                  </div>
                  <span
                    className={`card-check ${isOrganisationCardSelected ? 'card-check--on' : ''}`}
                    aria-hidden='true'
                  >
                    <CheckIcon />
                  </span>
                </div>
                {(organisationLimit || organisationAllowance) && (
                  <span
                    className='card-choice-note'
                    id='spend-sheet-organisation-limit'
                  >
                    {organisationLimit ?? organisationAllowance}
                  </span>
                )}
              </button>
            )}

            {/* Agent card — purchase flow, while the company still has one */}
            {agentCardOffered && (
              <button
                className={`card-choice ${isAgentCardSelected ? 'card-choice--selected' : ''} ${mixedCurrency ? 'card-choice--unavailable' : ''} card-network-agent`}
                onClick={() => setChosenCard(AGENT_CARD)}
                type='button'
                disabled={submitting || mixedCurrency}
                aria-pressed={isAgentCardSelected}
              >
                <div className='card-left'>
                  <CardAgentIcon />
                  <span className='card-nickname'>{cardLabel}</span>
                </div>
                <div className='card-right'>
                  {readiness ? (
                    <div className='card-right-column'>
                      <span className='card-balance-label'>Balance</span>
                      <span className='card-balance-value'>
                        {formatPrice(
                          readiness.balance,
                          readiness.currency ?? currency,
                        )}
                      </span>
                    </div>
                  ) : phase === 'checking' ? (
                    <span className='card-balance-loading'>…</span>
                  ) : null}
                  <span
                    className={`card-check ${isAgentCardSelected ? 'card-check--on' : ''}`}
                    aria-hidden='true'
                  >
                    <CheckIcon />
                  </span>
                </div>
              </button>
            )}

            {/* Saved user cards — hidden for in-person purchase actions */}
            {personalPaymentMethodsAvailable &&
              !dreamCardOnly &&
              accepts('user_card') &&
              savedCards.map((card) => {
                const isSelected = selectedCard === card.id;
                const cvcOnFile = cvcOnFileFor(card.id);
                const askCode =
                  isSelected && !!securePayment?.cvcRequired && !cvcOnFile;
                const codeLength = securityCodeLength(card.cardNetwork);
                const cardName = card.cardLastFour
                  ? `card ending ${card.cardLastFour}`
                  : 'this card';
                return (
                  <div
                    key={card.id}
                    className={`card-choice-row ${
                      askCode ? 'card-choice-row--with-code' : ''
                    }`}
                  >
                    <button
                      className={`card-choice ${isSelected ? 'card-choice--selected' : ''} card-network-${card.cardNetwork || 'other'}`}
                      onClick={() => {
                        if (!isSelected) setSecurityCode('');
                        setChosenCard(card.id);
                      }}
                      type='button'
                      disabled={submitting}
                      aria-pressed={isSelected}
                    >
                      <div className='card-left'>
                        {getCardIcon(card.cardNetwork)}
                        <span className='card-nickname'>{card.label}</span>
                      </div>
                      <div className='card-right'>
                        <span className='card-right-column'>
                          <span className='card-last-four'>
                            •••• {card.cardLastFour || '????'}
                          </span>
                          {cvcOnFile === true && (
                            <span
                              className='card-cvc-status'
                              data-testid='card-cvc-on-file'
                            >
                              CVV on file
                            </span>
                          )}
                        </span>
                        <span
                          className={`card-check ${isSelected ? 'card-check--on' : ''}`}
                          aria-hidden='true'
                        >
                          <CheckIcon />
                        </span>
                      </div>
                    </button>
                    {askCode && (
                      <div className='card-cvc-entry'>
                        <label htmlFor={`card-cvc-${card.id}`}>
                          Security code (CVV) for {cardName}
                        </label>
                        <input
                          id={`card-cvc-${card.id}`}
                          className='card-cvc-input'
                          type='password'
                          inputMode='numeric'
                          autoComplete='cc-csc'
                          maxLength={codeLength}
                          placeholder={'•'.repeat(codeLength)}
                          value={securityCode}
                          onChange={(e) =>
                            setSecurityCode(
                              e.target.value
                                .replace(/\D/g, '')
                                .slice(0, codeLength),
                            )
                          }
                          aria-describedby={`card-cvc-help-${card.id}`}
                        />
                        <p
                          id={`card-cvc-help-${card.id}`}
                          className='card-cvc-help'
                        >
                          {codeLength} digits{' '}
                          {card.cardNetwork === 'amex'
                            ? 'on the front'
                            : 'on the back'}{' '}
                          of the card. Kept with the card in your vault so
                          you're not asked again.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}

            {/* Add a payment card — hidden for in-person purchase actions */}
            {personalPaymentMethodsAvailable &&
              !dreamCardOnly &&
              accepts('user_card') && (
                <button
                  className='card-choice card-choice--add'
                  onClick={() => setShowAddCardModal(true)}
                  type='button'
                  disabled={submitting}
                >
                  <div className='card-left'>
                    <CreditCardPlusIcon />
                    <span className='card-nickname'>
                      Add a new payment card
                    </span>
                  </div>
                </button>
              )}

            {/* Merchant-stored option — only if merchantName is known, not deposit, not in-person */}
            {!isDeposit &&
              !dreamCardOnly &&
              accepts('merchant_stored') &&
              merchantName && (
                <>
                  <span className='spend-sheet-or-separator'>Or</span>
                  <button
                    className='card-choice card-choice--add'
                    onClick={() => {
                      setActiveSection('merchantStored');
                      setChosenCard('');
                    }}
                    type='button'
                    disabled={submitting}
                  >
                    <div className='card-left'>
                      <PasscodeIcon />
                      <span className='card-nickname'>
                        Use a payment method saved on {merchantName}
                      </span>
                    </div>
                  </button>
                </>
              )}
          </>
        ) : (
          <>
            {/* Collapsed saved-cards button */}
            {accepts('user_card') && (
              <button
                className='card-choice card-choice--add'
                onClick={() => {
                  setActiveSection('savedCards');
                  setChosenCard('');
                  setCardHint('');
                }}
                type='button'
              >
                <div className='card-left'>
                  <CardOtherIcon />
                  <span className='card-nickname'>Use your saved cards</span>
                </div>
              </button>
            )}

            {/* Merchant-stored card hint textarea */}
            <div className='spend-sheet-merchant-hint-section'>
              <label
                className='spend-sheet-merchant-hint-label'
                htmlFor='spend-sheet-card-hint'
              >
                If you have more than one, tell us which one
              </label>
              <textarea
                id='spend-sheet-card-hint'
                className='spend-sheet-card-hint-textarea'
                placeholder='Eg. Use my AMEX ending 1701'
                value={cardHint}
                onChange={(e) => setCardHint(e.target.value)}
                maxLength={500}
                rows={3}
              />
            </div>
          </>
        )}

        {dreamCardOnly && (
          <p className='spend-sheet-agent-only-note'>
            This purchase will be made in person using{' '}
            {isOrganisationCardSelected && organisation
              ? `a card funded by ${organisation.organisationName}`
              : cardLabel}
            .
          </p>
        )}
      </div>

      {/* Show the exact converted upfront charge in the organisation's currency. */}
      {isOrganisationCardSelected &&
        organisation &&
        (organisationLimit ? null : organisationUnavailable ? (
          <p
            className='spend-sheet-organisation-note spend-sheet-organisation-note--blocked'
            id='spend-sheet-organisation-reason'
          >
            {organisationUnavailable} Choose another way to pay below.
          </p>
        ) : organisation.requestedAmount !== null ? (
          <p className='spend-sheet-organisation-note'>
            {organisation.organisationName} is charged{' '}
            <strong>
              {formatOrganisationAmount(
                organisation.requestedAmount,
                organisation.currency,
              )}
            </strong>{' '}
            now. Anything the purchase doesn&apos;t use is refunded to the
            organisation.
          </p>
        ) : null)}

      {/* Shortfall message — only when agent card is selected and balance insufficient */}
      {!isDeposit &&
        isAgentCardSelected &&
        phase === 'topup-required' &&
        readiness && (
          <p className='spend-sheet-shortfall'>
            {readiness.billingSetupRequired ? (
              <>
                {cardLabel} needs another{' '}
                <strong className='spend-sheet-shortfall-amount'>
                  {formatPrice(
                    readiness.shortfall,
                    readiness.currency ?? currency,
                  )}
                </strong>
                . Add a card to top it up — no subscription needed.
              </>
            ) : (
              <>
                You need another{' '}
                <strong className='spend-sheet-shortfall-amount'>
                  {formatPrice(
                    readiness.shortfall,
                    readiness.currency ?? currency,
                  )}
                </strong>
                {' to complete this spend. Continue below to top-up.'}
              </>
            )}
          </p>
        )}

      {/* Deposit flow: negative balance banner */}
      {isDeposit && phase === 'topup-required' && depositReadiness && (
        <div className='spend-sheet-negative-banner'>
          {depositReadiness.billingSetupRequired ? (
            <>
              <span className='spend-sheet-negative-banner-bold'>
                {cardLabel} currently has a negative balance.
              </span>
              <br />
              Add a card below to clear it — no subscription needed.
            </>
          ) : (
            <>
              <span className='spend-sheet-negative-banner-bold'>
                {cardLabel} currently has a negative balance.
              </span>
              <br />
              To make this reservation you need to{' '}
              <span className='spend-sheet-negative-banner-amount'>
                add{' '}
                {formatPrice(
                  Math.ceil(depositReadiness.shortfall || 0),
                  depositReadiness.currency ?? currency,
                )}
              </span>
              .
              <br />
              {!sharedMode && 'Continue below to top up.'}
            </>
          )}
        </div>
      )}

      {errorMessage && (
        <div className='spend-sheet-error' role='alert'>
          {errorMessage}
        </div>
      )}

      {detailRows.length > 0 && (
        <div className='spend-sheet-summary'>{detailRows}</div>
      )}
    </div>
  );

  // Reject is a decision and is always offered; Cancel only when the sheet
  // can actually be dismissed (never on a standalone page).
  const secondaryAction = onReject ? (
    <button
      className='spend-sheet-reject-btn tx-btn-text'
      onClick={onReject}
      type='button'
      disabled={submitting}
    >
      Reject
    </button>
  ) : canDismiss ? (
    <button
      className='spend-sheet-cancel-btn tx-btn-text'
      onClick={onCancel}
      type='button'
      disabled={submitting}
    >
      Cancel
    </button>
  ) : null;

  const organisationProgressFooter =
    !organisationProgress ? null : organisationProgress.stage === 'failed' ? (
      <div className='org-progress org-progress--failed'>
        <CreditCardXIcon
          className='org-progress-icon'
          width={64}
          height={64}
          aria-hidden='true'
        />
        <div className='org-progress-text' role='alert'>
          <p className='org-progress-title org-progress-title--failed'>
            {organisationProgress.title}
          </p>
          <p className='org-progress-body'>{organisationProgress.body}</p>
        </div>
        <button
          className='spend-sheet-approve-btn tx-btn-primary'
          type='button'
          onClick={() => setOrganisationProgress(null)}
        >
          Choose another card
        </button>
        {organisation?.available && (
          <button
            className='spend-sheet-cancel-btn tx-btn-text'
            type='button'
            onClick={handleApproveWithOrganisation}
          >
            Retry on this card
          </button>
        )}
      </div>
    ) : (
      <div className='org-progress' aria-live='polite'>
        <img
          className='org-progress-logo'
          src='/dream-loading.svg'
          alt=''
          width='72'
          height='72'
        />
        <div className='org-progress-text' key={organisationProgress.stage}>
          <p className='org-progress-title'>
            {organisationProgress.stage === 'approving'
              ? 'Approving with your organisation'
              : 'Checking payment'}
          </p>
          <p className='org-progress-body'>
            {organisationProgress.stage === 'approving'
              ? "This may take a moment, don't close this window."
              : "We're almost done, keep this window open."}
          </p>
        </div>
      </div>
    );

  const spendFooter = organisationProgressFooter ?? (
    <>
      {fundingTrayActive ? (
        <MorphButton
          className='spend-sheet-approve-btn tx-btn-primary'
          onClick={action.onClick}
          disabled={action.disabled}
          type='button'
        >
          {action.label}
        </MorphButton>
      ) : (
        <button
          className='spend-sheet-approve-btn tx-btn-primary'
          onClick={action.onClick}
          disabled={action.disabled}
          type='button'
        >
          {action.label}
        </button>
      )}
      {secondaryAction}
    </>
  );

  const addFundsTray =
    fundingTrayActive && topUpOption ? (
      <AddFundsTray
        open={fundingOpen}
        footerRef={spendFooterRef}
        cardName={cardLabel}
        amountLabel={formatPrice(
          topUpOption.totalCredits,
          topUpOption.currency,
        )}
        cards={fundingCards}
        cardsLoading={fundingCardsLoading}
        selectedCardId={fundingCardId}
        onSelectCard={handleSelectFundingCard}
        step={fundingStep}
        onStepChange={setFundingStep}
        startCardSetup={startFundingCardSetup}
        onCardSaved={handleFundingCardSaved}
        onAddFunds={handleAddFunds}
        onCancel={() => setFundingOpen(false)}
        errorMessage={errorMessage}
      />
    ) : null;

  // Add-card is stacked on top of this sheet. Back returns here keeping
  // everything typed so far; Close abandons the whole approval (only when
  // this sheet is itself dismissible).
  const addCardSheet = (
    <AddPaymentCardModal
      show={showAddCardModal}
      presentation={presentation}
      dismissible={canDismiss}
      onBack={() => setShowAddCardModal(false)}
      onClose={() => {
        setShowAddCardModal(false);
        onCancel();
      }}
      onSuccess={handleAddCardSuccess}
      shareToken={shareToken}
    />
  );

  // Logged out: a redirect to login is in flight (see effect above). Render
  // nothing rather than the onboarding/spend sheet, which would otherwise loop.
  if (needsLogin) return null;

  if (inline) {
    if (!show) return null;
    return (
      <>
        {showOnboarding && (
          <SpendingOnboardingModal
            show
            onNext={() => setShowOnboarding(false)}
            dreamCardOnly={dreamCardOnly}
          />
        )}
        {!showOnboarding && !showAddCardModal && (
          <>
            <div className='spend-sheet-shroud' />
            <div
              className={`tx-sheet spending-approval-modal spend-sheet-inline ${organisationProgress ? 'spend-sheet--org-progress' : ''}`}
              role='dialog'
              aria-labelledby='spend-sheet-title'
            >
              <div className='tx-card'>
                <div className='tx-body'>
                  <div className='tx-controls'>
                    <span className='tx-leading' aria-hidden='true'>
                      <ShoppingBagIcon />
                    </span>
                    {canDismiss && !submitting && (
                      <button
                        className='tx-control tx-control--close'
                        type='button'
                        aria-label='Close'
                        onClick={onCancel}
                      >
                        <XCloseIcon />
                      </button>
                    )}
                  </div>
                  {spendHead}
                  {spendBody}
                </div>
                <div className='tx-footer'>{spendFooter}</div>
              </div>
            </div>
          </>
        )}
        {addCardSheet}
      </>
    );
  }

  return (
    <>
      <SpendingOnboardingModal
        show={show && showOnboarding}
        onNext={() => setShowOnboarding(false)}
        dreamCardOnly={dreamCardOnly}
        presentation={presentation}
      />
      <TransactionalSheet
        show={show && !showAddCardModal && !showOnboarding}
        presentation={presentation}
        holdOpen={!!organisationProgress}
        onClose={canDismiss && !submitting ? onCancel : undefined}
        leading={<ShoppingBagIcon />}
        ariaLabelledBy='spend-sheet-title'
        className={`spending-approval-modal ${organisationProgress ? 'spend-sheet--org-progress' : ''}`}
        backdropClassName='spending-approval-backdrop'
        footer={spendFooter}
        footerRef={spendFooterRef}
        footerClassName={
          fundingOpen ? 'csd-footer--tray csd-footer--hidden' : undefined
        }
        afterFooter={addFundsTray}
      >
        {spendHead}
        {spendBody}
      </TransactionalSheet>
      {addCardSheet}
    </>
  );
};

export default SpendingApprovalModal;
